import { LogData } from '@1js/ai-hub-telemetry';
import * as React from 'react';
export var useModuleRenderTelemetryLogger = function (moduleSessionId, componentName, hostCallbacks, logger) {
    var perfActivityNames;
    (function (perfActivityNames) {
        perfActivityNames["InitialRender"] = "Module.Activity.InitialRender";
        perfActivityNames["TimeToInteractive"] = "Module.Activity.TimeToInteractive";
        perfActivityNames["FullRender"] = "Module.Activity.FullRender";
        perfActivityNames["Runtime"] = "Module.Activity.Runtime";
    })(perfActivityNames || (perfActivityNames = {}));
    var initialRenderActivityRef = React.useRef(undefined);
    var timeToInteractiveActivityRef = React.useRef(undefined);
    var fullRenderActivityRef = React.useRef(undefined);
    var runtimeActivityRef = React.useRef(undefined);
    if (initialRenderActivityRef.current === undefined &&
        timeToInteractiveActivityRef.current === undefined &&
        fullRenderActivityRef.current === undefined &&
        runtimeActivityRef.current === undefined) {
        var startAndLogActivity = function (activityName) {
            var activity = logger === null || logger === void 0 ? void 0 : logger.startActivity(activityName);
            return activity;
        };
        initialRenderActivityRef.current = startAndLogActivity(perfActivityNames.InitialRender);
        timeToInteractiveActivityRef.current = startAndLogActivity(perfActivityNames.TimeToInteractive);
        fullRenderActivityRef.current = startAndLogActivity(perfActivityNames.FullRender);
        runtimeActivityRef.current = startAndLogActivity(perfActivityNames.Runtime);
    }
    var onModuleRenderCallback = React.useCallback(function (renderType) {
        if (renderType === 'initialRender' &&
            initialRenderActivityRef.current) {
            initialRenderActivityRef.current.endSuccessful();
            initialRenderActivityRef.current = null;
        }
        else if (renderType === 'timeToInteractive' &&
            timeToInteractiveActivityRef.current) {
            timeToInteractiveActivityRef.current.endSuccessful();
            timeToInteractiveActivityRef.current = null;
        }
        else if (renderType === 'fullRender' &&
            fullRenderActivityRef.current) {
            fullRenderActivityRef.current.endSuccessful();
            fullRenderActivityRef.current = null;
        }
    }, []);
    // Runtime ends on dismount. Clean up all other activities as well if they are active
    React.useEffect(function () {
        return function () {
            var markRenderActivityAsIncomplete = function (activityRef) {
                if (activityRef.current) {
                    activityRef.current.addLogData([
                        new LogData('Incomplete', true),
                    ]);
                    activityRef.current.endFailure();
                    activityRef.current = null;
                }
            };
            // Mark render activities as failures so they don't linger if module unloads.
            // Add log data so these events can be filtered out in the telemetry pipeline.
            markRenderActivityAsIncomplete(initialRenderActivityRef);
            markRenderActivityAsIncomplete(timeToInteractiveActivityRef);
            markRenderActivityAsIncomplete(fullRenderActivityRef);
            // Mark runtime activity as success
            if (runtimeActivityRef.current) {
                runtimeActivityRef.current.endSuccessful();
                runtimeActivityRef.current = null;
            }
        };
    }, []);
    var onCaughtError = React.useCallback(function (error, details) {
        var endActivityOnFailure = function (activityRef) {
            if (activityRef.current) {
                activityRef.current.addErrorDataToActivity({ error: error });
                activityRef.current.endFailure();
                activityRef.current = null;
            }
        };
        // Mark activities as failed if they are still active
        endActivityOnFailure(initialRenderActivityRef);
        endActivityOnFailure(timeToInteractiveActivityRef);
        endActivityOnFailure(fullRenderActivityRef);
        endActivityOnFailure(runtimeActivityRef);
        // Send Error
        logger === null || logger === void 0 ? void 0 : logger.sendError({
            errorName: 'Application.Error.CriticalFailure',
            error: error,
        });
        // TODO (9290423): reportCriticalFailure used to take a string as its second param, but was later expanded to use a complex type. Remove this block of code once deprecation is complete.
        var errDetails = {};
        if (typeof details === 'string') {
            errDetails.category = details;
        }
        else {
            errDetails = details || {};
        }
        errDetails.moduleName = componentName;
        errDetails.moduleSessionId = moduleSessionId;
        hostCallbacks === null || hostCallbacks === void 0 ? void 0 : hostCallbacks.reportCriticalFailure(error, errDetails);
    }, [componentName, hostCallbacks, logger, moduleSessionId]);
    React.useEffect(function () {
        // Log correlation vector for module being loaded
        logger === null || logger === void 0 ? void 0 : logger.sendEvent('AiHub.Event.ModuleInitialized', new LogData('ModuleSessionId', moduleSessionId), new LogData('ModuleName', componentName !== null && componentName !== void 0 ? componentName : 'Unknown'));
        // Send user action event on module load
        var details = {
            name: 'ModuleSelected',
            isExportable: true,
            isIntentional: true,
        };
        logger === null || logger === void 0 ? void 0 : logger.sendUserActionEvent('AiHub.Event.ModuleSelected', details, new LogData('ModuleSessionId', moduleSessionId), new LogData('ModuleName', componentName !== null && componentName !== void 0 ? componentName : 'Unknown'));
        // Do not add any dependencies to ensure this does not run more times than aniticipated
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return { onCaughtError: onCaughtError, onModuleRenderCallback: onModuleRenderCallback };
};
//# sourceMappingURL=useModuleRenderTelemetryLogger.js.map